import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Target, Flame, Zap,
  TrendingUp, TrendingDown, Minus, AlertCircle, Award,
  CheckCircle2, Circle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { API_BASE } from '@/services/api';
import managedAxios from '@/utils/managedAxios';

// Status -> visual style. Keep colors aligned with the rest of Strategy
// Dashboard: emerald = strong, orange = weak, red = critical, slate = idle.
const STATUS_STYLES = {
  STRONG:     { dot: 'bg-emerald-500', text: 'text-emerald-700', pill: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'STRONG' },
  GOOD:       { dot: 'bg-teal-500',    text: 'text-teal-700',    pill: 'bg-teal-100 text-teal-700 border-teal-200',          label: 'GOOD' },
  AVERAGE:    { dot: 'bg-amber-500',   text: 'text-amber-700',   pill: 'bg-amber-100 text-amber-800 border-amber-200',       label: 'AVERAGE' },
  WEAK:       { dot: 'bg-orange-500',  text: 'text-orange-700',  pill: 'bg-orange-100 text-orange-800 border-orange-200',    label: 'WEAK' },
  CRITICAL:   { dot: 'bg-red-500',     text: 'text-red-700',     pill: 'bg-red-100 text-red-700 border-red-200',             label: 'CRITICAL' },
  LOW_SAMPLE: { dot: 'bg-slate-400',   text: 'text-slate-600',   pill: 'bg-slate-100 text-slate-600 border-slate-200',       label: 'LOW SAMPLE' },
  NOT_STARTED:{ dot: 'bg-slate-200',   text: 'text-slate-500',   pill: 'bg-slate-50 text-slate-500 border-slate-200',        label: 'NOT STARTED' },
};

const formatRelative = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

const TrendIcon = ({ trend, delta }) => {
  if (trend === 'improving') return <span className="inline-flex items-center gap-0.5 text-emerald-600 text-xs font-medium" title={`+${delta}% recent vs prior`}><TrendingUp className="w-3 h-3" />{Math.abs(delta)}%</span>;
  if (trend === 'declining') return <span className="inline-flex items-center gap-0.5 text-red-600 text-xs font-medium" title={`${delta}% recent vs prior`}><TrendingDown className="w-3 h-3" />{Math.abs(delta)}%</span>;
  if (trend === 'stable')    return <span className="inline-flex items-center gap-0.5 text-slate-500 text-xs" title="Stable"><Minus className="w-3 h-3" />stable</span>;
  return null;
};

export const CsatPerformanceIntelligence = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // bucket key -> bool

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await managedAxios.get(
          `${API_BASE}/student-strategy/csat-performance?days=120`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) {
          setData(res.data);
          // Auto-expand buckets with practice so user sees insights immediately
          const init = {};
          (res.data.buckets || []).forEach(b => {
            if (b.attempts > 0) init[b.key] = true;
          });
          setExpanded(init);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const goPractice = (subtopicKey, isPyq) => {
    if (isPyq) {
      navigate('/csat-suite?mode=pyq-analysis');
    } else {
      navigate(`/csat-suite?mode=universal-practice&topic=${encodeURIComponent(subtopicKey)}`);
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-md" data-testid="csat-performance-intelligence-loading">
        <CardContent className="py-10 text-center text-slate-500 text-sm">
          Loading CSAT performance intelligence…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="border-0 shadow-md" data-testid="csat-performance-intelligence-error">
        <CardContent className="py-6 text-center text-red-600 text-sm">{error}</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { summary = {}, buckets = [] } = data;
  const practicedBuckets = buckets.filter(b => b.attempts > 0);
  const unpracticedBuckets = buckets.filter(b => b.attempts === 0);

  if (summary.total_attempts === 0) {
    return (
      <Card className="border-0 shadow-md" data-testid="csat-performance-intelligence-empty">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-500" />
            CSAT Performance Intelligence
          </CardTitle>
        </CardHeader>
        <CardContent className="py-10 text-center">
          <p className="font-semibold text-slate-700 mb-1">No CSAT practice yet</p>
          <p className="text-sm text-slate-500 mb-4">
            Practice any chapter or take a CSAT mock to start building your sub-topic intelligence map.
          </p>
          <Button data-testid="csat-empty-cta" onClick={() => navigate('/csat-suite?mode=universal-practice')}>
            Start CSAT practice →
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-md" data-testid="csat-performance-intelligence">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-t-lg">
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-500" />
          CSAT Performance Intelligence
          <Badge variant="outline" className="ml-auto text-xs bg-white">
            {summary.total_subtopics_practiced} sub-topics · {summary.total_attempts} Qs
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-5 space-y-5">
        {/* Summary chips */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[
            { key: 'strong',     label: 'Strong',    val: summary.strong,    style: STATUS_STYLES.STRONG },
            { key: 'good',       label: 'Good',      val: summary.good,      style: STATUS_STYLES.GOOD },
            { key: 'average',    label: 'Average',   val: summary.average,   style: STATUS_STYLES.AVERAGE },
            { key: 'weak',       label: 'Weak',      val: summary.weak,      style: STATUS_STYLES.WEAK },
            { key: 'critical',   label: 'Critical',  val: summary.critical,  style: STATUS_STYLES.CRITICAL },
            { key: 'low_sample', label: 'Low data',  val: summary.low_sample,style: STATUS_STYLES.LOW_SAMPLE },
          ].map(c => (
            <div
              key={c.key}
              data-testid={`csat-summary-${c.key}`}
              className={`px-2 py-2 rounded-lg border ${c.style.pill} flex items-center justify-between`}
            >
              <span className="text-xs font-medium">{c.label}</span>
              <span className="text-lg font-bold">{c.val || 0}</span>
            </div>
          ))}
        </div>

        {/* Top action items banner */}
        {summary.top_action_items && summary.top_action_items.length > 0 && (
          <div
            className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-4"
            data-testid="csat-action-banner"
          >
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-4 h-4 text-orange-600" />
              <p className="text-sm font-semibold text-orange-900">Focus on these {summary.top_action_items.length} weakest chapters first</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              {summary.top_action_items.map(item => (
                <button
                  key={item.key}
                  onClick={() => goPractice(item.key, item.is_pyq)}
                  data-testid={`csat-action-${item.key}`}
                  className="text-left bg-white hover:bg-orange-50 transition-colors rounded-lg p-3 border border-orange-100 hover:border-orange-300 group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-800 truncate">{item.name}</span>
                    <Zap className="w-3 h-3 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className={STATUS_STYLES[item.status]?.text || ''}>{item.accuracy}% acc</span>
                    <span>·</span>
                    <span>{item.attempts} Qs</span>
                  </div>
                  <div className="mt-2 text-xs font-medium text-orange-700 group-hover:text-orange-800">
                    Practice 5 Qs →
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Practiced buckets — accordion */}
        <div className="space-y-2">
          {practicedBuckets.map(bucket => {
            const isOpen = !!expanded[bucket.key];
            return (
              <div key={bucket.key} className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`csat-bucket-${bucket.key}`}>
                <button
                  onClick={() => setExpanded(s => ({ ...s, [bucket.key]: !isOpen }))}
                  data-testid={`csat-bucket-toggle-${bucket.key}`}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  {isOpen
                    ? <ChevronDown className="w-4 h-4 text-slate-500" />
                    : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <span className="font-semibold text-slate-800 text-sm">{bucket.name}</span>
                  <Badge variant="outline" className="text-xs bg-white">{bucket.mastery}/100</Badge>
                  <span className="ml-auto flex items-center gap-3 text-xs text-slate-600">
                    <span>{bucket.accuracy}% acc</span>
                    <span>·</span>
                    <span>{bucket.attempts} Qs</span>
                    <span>·</span>
                    <span>{bucket.subtopic_count} chapters</span>
                  </span>
                </button>
                {isOpen && (
                  <ul className="divide-y divide-slate-100 bg-white">
                    {bucket.subtopics.map(st => {
                      const s = STATUS_STYLES[st.status] || STATUS_STYLES.NOT_STARTED;
                      return (
                        <li
                          key={st.key}
                          data-testid={`csat-subtopic-${st.key}`}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50"
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-slate-800 truncate">{st.name}</span>
                              <Badge className={`text-[10px] border ${s.pill}`} variant="outline">{s.label}</Badge>
                              <TrendIcon trend={st.trend} delta={st.trend_delta} />
                              {st.is_pyq && <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">PYQ</Badge>}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                              <span className={s.text}>{st.accuracy}% acc</span>
                              <span>·</span>
                              <span>{st.attempts} Qs ({st.correct}✓ {st.wrong}✗{st.skipped > 0 ? ` ${st.skipped}–` : ''})</span>
                              <span>·</span>
                              <span>Mastery {st.mastery}/100</span>
                              <span>·</span>
                              <span>Last: {formatRelative(st.last_practiced)}</span>
                            </div>
                          </div>
                          {!st.is_pyq && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`csat-practice-btn-${st.key}`}
                              onClick={() => goPractice(st.key, st.is_pyq)}
                              className="text-xs hidden sm:inline-flex"
                            >
                              Practice 5 Qs →
                            </Button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {/* Untouched buckets */}
        {unpracticedBuckets.length > 0 && (
          <div className="pt-2">
            <p className="text-xs font-medium text-slate-500 mb-2">Untouched ({unpracticedBuckets.length})</p>
            <div className="flex flex-wrap gap-2">
              {unpracticedBuckets.map(b => (
                <Badge
                  key={b.key}
                  variant="outline"
                  data-testid={`csat-untouched-${b.key}`}
                  className="text-xs bg-white text-slate-500 border-slate-200"
                >
                  <Circle className="w-2 h-2 mr-1" />
                  {b.name}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CsatPerformanceIntelligence;
